import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Palace, Star } from '@/lib/ziwei-calculator';

interface ChartDisplayProps {
  palaces: Palace[];
  bodyPalace: number;
}

const ChartDisplay: React.FC<ChartDisplayProps> = ({ palaces, bodyPalace }) => {
  // 獲取星曜顏色
  const getStarColor = (star: Star) => {
    switch (star.type) {
      case 'main':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'auxiliary':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'four_stars':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // 獲取亮度顏色
  const getBrightnessColor = (brightness: Star['brightness']) => {
    switch (brightness) {
      case '廟':
        return 'text-red-600 font-bold';
      case '旺':
        return 'text-orange-600 font-semibold';
      case '得':
        return 'text-yellow-600';
      case '利':
        return 'text-green-600';
      case '平':
        return 'text-blue-600';
      case '不':
        return 'text-gray-600';
      case '陷':
        return 'text-gray-400';
      default:
        return 'text-gray-600';
    }
  };

  // 命盤佈局 - 使用傳統的十二宮排列
  const getGridPosition = (position: number) => {
    const positions = [
      'col-start-4 row-start-1', // 1. 巳宮
      'col-start-3 row-start-1', // 2. 辰宮
      'col-start-2 row-start-1', // 3. 卯宮
      'col-start-1 row-start-1', // 4. 寅宮
      'col-start-1 row-start-2', // 5. 丑宮
      'col-start-1 row-start-3', // 6. 子宮
      'col-start-1 row-start-4', // 7. 亥宮
      'col-start-2 row-start-4', // 8. 戌宮
      'col-start-3 row-start-4', // 9. 酉宮
      'col-start-4 row-start-4', // 10. 申宮
      'col-start-4 row-start-3', // 11. 未宮
      'col-start-4 row-start-2'  // 12. 午宮
    ];
    return positions[position - 1];
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <div className="grid grid-cols-4 grid-rows-4 gap-2 aspect-square max-w-4xl mx-auto">
        {palaces.map((palace) => (
          <Card 
            key={palace.position} 
            className={`${getGridPosition(palace.position)} relative border-2 ${
              palace.position === bodyPalace ? 'border-red-400 bg-red-50' : 'border-gray-300'
            }`}
          >
            <CardHeader className="p-2 pb-1">
              <CardTitle className="text-xs font-bold text-center">
                {palace.name}
                {palace.position === bodyPalace && (
                  <Badge variant="destructive" className="ml-1 text-xs">身</Badge>
                )}
              </CardTitle>
              <div className="text-xs text-center text-gray-600">
                {palace.heavenlyStem}{palace.earthlyBranch}
              </div>
            </CardHeader>
            <CardContent className="p-2 pt-0">
              <div className="space-y-1">
                {palace.stars.map((star, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${getStarColor(star)}`}
                    >
                      {star.name}
                    </Badge>
                    <span className={`text-xs ${getBrightnessColor(star.brightness)}`}>
                      {star.brightness}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* 圖例 */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">星曜類型</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-purple-100 text-purple-800 border-purple-300">主星</Badge>
              <span className="text-xs">十四主星</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-800 border-blue-300">輔星</Badge>
              <span className="text-xs">輔助星曜</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800 border-green-300">四化</Badge>
              <span className="text-xs">四化星</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">星曜亮度</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-red-600 font-bold text-xs">廟</span>
              <span className="text-orange-600 font-semibold text-xs">旺</span>
              <span className="text-yellow-600 text-xs">得</span>
              <span className="text-green-600 text-xs">利</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-blue-600 text-xs">平</span>
              <span className="text-gray-600 text-xs">不</span>
              <span className="text-gray-400 text-xs">陷</span>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">特殊標記</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="destructive" className="text-xs">身</Badge>
              <span className="text-xs">身宮位置</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-red-400 bg-red-50"></div>
              <span className="text-xs">身宮所在宮位</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChartDisplay;